import type { DEFAULT_TRACKS } from "./timeline";

/**
 * La banque de questions de Ma timeline (spec docs/idees/timeline-vie/05-spec.md, section 3.6 ;
 * ticket 5, #512). Premier jet ecrit par l'agent le 23 octobre 2026, **a relire par Nicolas avant
 * la mise en ligne** (geste `relire-questions` de l'idee `timeline-vie`).
 *
 * Regles d'ecriture :
 * - le ton d'un ami qui demande (« Ta premiere fois en avion, c'etait pour aller ou ? ») ;
 * - aucune question sur la sante, les deces ou la sexualite nommee crument (un « premier baiser »
 *   reste permis) ; `test/app-questions.test.ts` refuse une liste de mots ;
 * - un identifiant stable (`piste-etape-sujet`), qui ne change plus une fois en ligne : l'etat d'une
 *   question (repondue, passee, plus tard) est range en base sous cet identifiant. Retirer une
 *   question est sans danger ; renommer son identifiant la ferait reposer.
 * - les identifiants `concert-*` sont reserves aux questions des concerts, fabriquees a la volee
 *   (`question-draw.ts`).
 */

/** Les periodes de vie d'une question, et leurs ages (inclus) : 25 ans et plus pour l'adulte. */
export const LIFE_STAGES = ["enfance", "college", "lycee", "etudes", "adulte"] as const;
export type LifeStage = (typeof LIFE_STAGES)[number];

export const LIFE_STAGE_AGES: Record<LifeStage, { min: number; max: number | null }> = {
  enfance: { min: 0, max: 10 },
  college: { min: 11, max: 14 },
  lycee: { min: 15, max: 17 },
  etudes: { min: 18, max: 24 },
  adulte: { min: 25, max: null },
};

/** Le nom d'une periode dans la jauge « Ou ta frise est encore vide ». */
export const LIFE_STAGE_NAMES: Record<LifeStage, string> = {
  enfance: "Enfance",
  college: "Collège",
  lycee: "Lycée",
  etudes: "Études",
  adulte: "Adulte",
};

/** La periode a cote de la piste, sur la carte d'une question (« Études · le collège »). */
export const LIFE_STAGE_CHIPS: Record<LifeStage, string> = {
  enfance: "l'enfance",
  college: "le collège",
  lycee: "le lycée",
  etudes: "18-24 ans",
  adulte: "l'âge adulte",
};

/**
 * Le « Quand ? » prerempli quand on repond a une question d'une periode, seulement pour les
 * periodes que `parseFuzzyDate` lit comme une etape scolaire. L'enfance (0-10 ans) n'a pas
 * d'equivalent exact (« en primaire » ne couvre que 6-10 ans), l'adulte non plus : champ vide.
 */
export const LIFE_STAGE_WHEN: Partial<Record<LifeStage, string>> = {
  college: "au collège",
  lycee: "au lycée",
  etudes: "à la fac",
};

/** Ce qu'une question attend : un moment ou une periode. */
export const QUESTION_EXPECTS = ["point", "periode"] as const;
export type QuestionExpects = (typeof QUESTION_EXPECTS)[number];

/** Les pistes que vise une question : les huit pistes par defaut. */
export type TrackSlug = (typeof DEFAULT_TRACKS)[number]["slug"];

export type Question = {
  /** Slug stable, `^[a-z0-9-]{1,64}$` (la contrainte de `question_state.question_id`). */
  id: string;
  text: string;
  track: TrackSlug;
  lifeStage?: LifeStage;
  expects: QuestionExpects;
  /** Seulement pour une question de concert : le point « a dater » qu'une reponse date. */
  entryId?: number;
};

/** Une question de la banque, ecrite en une ligne : piste, periode (ou `null`), forme, slug, texte. */
const q = (
  track: TrackSlug,
  lifeStage: LifeStage | null,
  expects: QuestionExpects,
  id: string,
  text: string,
): Question =>
  lifeStage === null ? { id, text, track, expects } : { id, text, track, lifeStage, expects };

export const QUESTIONS: readonly Question[] = [
  // --- Amour -----------------------------------------------------------------------------------
  q(
    "amour",
    "enfance",
    "point",
    "amour-enfance-amoureux",
    "À l'école primaire, tu avais un amoureux ou une amoureuse ?",
  ),
  q(
    "amour",
    "college",
    "point",
    "amour-college-premier-baiser",
    "Ton premier baiser, c'était où, et vers quel âge ?",
  ),
  q(
    "amour",
    "college",
    "periode",
    "amour-college-premier-amour",
    "Ton premier amour, ça a duré de quand à quand ?",
  ),
  q(
    "amour",
    "college",
    "point",
    "amour-college-mot-doux",
    "Un mot doux glissé dans un casier ou une trousse, tu t'en souviens ?",
  ),
  q(
    "amour",
    "lycee",
    "periode",
    "amour-lycee-premiere-relation",
    "Ta première vraie relation, elle a commencé quand ?",
  ),
  q(
    "amour",
    "lycee",
    "point",
    "amour-lycee-chagrin",
    "Ton premier gros chagrin d'amour, c'était quand ?",
  ),
  q(
    "amour",
    "lycee",
    "point",
    "amour-lycee-slow",
    "Une boum où tu as dansé un slow, c'était quelle année ?",
  ),
  q(
    "amour",
    "etudes",
    "point",
    "amour-etudes-rencontre",
    "Pendant tes études, une rencontre qui a compté, c'était quand ?",
  ),
  q(
    "amour",
    "etudes",
    "periode",
    "amour-etudes-histoire",
    "Une histoire d'amour pendant tes études, de quand à quand ?",
  ),
  q(
    "amour",
    "etudes",
    "periode",
    "amour-etudes-distance",
    "Tu as vécu un amour à distance ? C'était à quelle période ?",
  ),
  q(
    "amour",
    "adulte",
    "point",
    "amour-adulte-rencontre",
    "Comment as-tu rencontré la personne qui partage ta vie, et quand ?",
  ),
  q(
    "amour",
    "adulte",
    "point",
    "amour-adulte-emmenager",
    "La première fois que tu as emménagé avec quelqu'un, c'était quand ?",
  ),
  q(
    "amour",
    "adulte",
    "point",
    "amour-adulte-demande",
    "Une demande en mariage, faite ou reçue ? Raconte le jour.",
  ),
  q("amour", "adulte", "point", "amour-adulte-mariage", "Ton mariage ou ton PACS, c'était quand ?"),
  q(
    "amour",
    "adulte",
    "periode",
    "amour-adulte-relation-longue",
    "Ta plus longue histoire, elle a commencé quand ?",
  ),
  q(
    "amour",
    "adulte",
    "point",
    "amour-adulte-rupture",
    "Une rupture qui t'a fait grandir, c'était quand ?",
  ),
  q(
    "amour",
    "adulte",
    "point",
    "amour-adulte-voyage-amoureux",
    "Ton premier voyage en amoureux, c'était où ?",
  ),
  q(
    "amour",
    null,
    "point",
    "amour-coup-de-foudre",
    "Un coup de foudre, tu en as eu un ? C'était quand ?",
  ),
  q(
    "amour",
    null,
    "point",
    "amour-premier-rendez-vous",
    "Un premier rendez-vous dont tu te souviens encore, c'était où ?",
  ),
  q(
    "amour",
    null,
    "periode",
    "amour-celibat",
    "Une période de célibat que tu as bien aimée, c'était quand ?",
  ),
  q(
    "amour",
    null,
    "point",
    "amour-chanson",
    "Une chanson qui te fait penser à quelqu'un, elle date de quand ?",
  ),

  // --- Famille ---------------------------------------------------------------------------------
  q(
    "famille",
    "enfance",
    "point",
    "famille-enfance-premier-souvenir",
    "Ton tout premier souvenir, tu avais quel âge ?",
  ),
  q(
    "famille",
    "enfance",
    "periode",
    "famille-enfance-grands-parents",
    "Les vacances chez tes grands-parents, c'était où, et jusqu'à quel âge ?",
  ),
  q(
    "famille",
    "enfance",
    "point",
    "famille-enfance-frere-soeur",
    "L'arrivée d'un petit frère ou d'une petite sœur, tu t'en souviens ?",
  ),
  q(
    "famille",
    "enfance",
    "point",
    "famille-enfance-animal",
    "Ton premier animal de compagnie, il est arrivé quand ?",
  ),
  q(
    "famille",
    "enfance",
    "point",
    "famille-enfance-noel",
    "Un Noël d'enfance qui t'a marqué, c'était quelle année ?",
  ),
  q(
    "famille",
    "enfance",
    "point",
    "famille-enfance-velo",
    "Tu as appris à faire du vélo vers quel âge, et avec qui ?",
  ),
  q("famille", "enfance", "point", "famille-enfance-nager", "Tu as appris à nager quand, et où ?"),
  q(
    "famille",
    "enfance",
    "point",
    "famille-enfance-cadeau",
    "Le cadeau dont tu as le plus rêvé, tu l'as eu à quel âge ?",
  ),
  q(
    "famille",
    "college",
    "periode",
    "famille-college-cousins",
    "Des étés avec tes cousins, c'était à quelle période ?",
  ),
  q(
    "famille",
    "college",
    "point",
    "famille-college-argent-poche",
    "Ton premier argent de poche, c'était vers quel âge ?",
  ),
  q(
    "famille",
    "lycee",
    "point",
    "famille-lycee-conduite",
    "Qui t'a appris à conduire, et tu as eu ton permis quand ?",
  ),
  q(
    "famille",
    "lycee",
    "point",
    "famille-lycee-repas",
    "Un repas de famille mémorable pendant tes années de lycée ?",
  ),
  q(
    "famille",
    "etudes",
    "point",
    "famille-etudes-retour-week-end",
    "Les week-ends où tu rentrais chez tes parents, ça a duré jusqu'à quand ?",
  ),
  q(
    "famille",
    "adulte",
    "point",
    "famille-adulte-premier-enfant",
    "La naissance de ton premier enfant, c'était quand ?",
  ),
  q(
    "famille",
    "adulte",
    "point",
    "famille-adulte-famille-recomposee",
    "Un enfant entré dans ta vie par une adoption ou une famille recomposée, c'était quand ?",
  ),
  q(
    "famille",
    "adulte",
    "point",
    "famille-adulte-parrain",
    "Tu es devenu parrain ou marraine ? De qui, et quand ?",
  ),
  q(
    "famille",
    "adulte",
    "point",
    "famille-adulte-neveu",
    "La naissance d'un neveu ou d'une nièce, c'était quand ?",
  ),
  q(
    "famille",
    "adulte",
    "point",
    "famille-adulte-grande-fete",
    "Une grande fête de famille, c'était quelle année ?",
  ),
  q(
    "famille",
    "adulte",
    "point",
    "famille-adulte-mariage-famille",
    "Un mariage dans ta famille où tu as dansé jusqu'au bout de la nuit ?",
  ),
  q(
    "famille",
    null,
    "point",
    "famille-tradition",
    "Une tradition de famille que tu as gardée, elle remonte à quand ?",
  ),
  q(
    "famille",
    null,
    "point",
    "famille-recette",
    "Une recette de famille que tu as appris à faire, c'était quand ?",
  ),
  q(
    "famille",
    null,
    "point",
    "famille-photo",
    "Une photo de famille que tu aimes bien, elle date de quand ?",
  ),
  q(
    "famille",
    null,
    "point",
    "famille-animal",
    "Un animal qui a partagé ta vie, il est arrivé quand ?",
  ),

  // --- Études ----------------------------------------------------------------------------------
  q(
    "etudes",
    "enfance",
    "point",
    "etudes-enfance-maternelle",
    "Ta maîtresse ou ton maître de maternelle, tu te souviens de son nom ?",
  ),
  q(
    "etudes",
    "enfance",
    "point",
    "etudes-enfance-cp",
    "Ton entrée au CP, c'était dans quelle école ?",
  ),
  q(
    "etudes",
    "enfance",
    "periode",
    "etudes-enfance-primaire",
    "Ton école primaire, tu y es resté de quand à quand ?",
  ),
  q(
    "etudes",
    "enfance",
    "point",
    "etudes-enfance-classe-verte",
    "Une classe verte ou une classe de neige, c'était en quelle classe ?",
  ),
  q(
    "etudes",
    "enfance",
    "point",
    "etudes-enfance-premier-livre",
    "Le premier livre que tu as lu tout seul, c'était vers quel âge ?",
  ),
  q(
    "etudes",
    "college",
    "point",
    "etudes-college-3e-ambiance",
    "En 3e, tu étais dans quel collège, et tu t'y plaisais ?",
  ),
  q(
    "etudes",
    "college",
    "point",
    "etudes-college-entree-6e",
    "Ton entrée en 6e, ça s'est passé comment ?",
  ),
  q(
    "etudes",
    "college",
    "point",
    "etudes-college-prof",
    "Un prof du collège qui t'a marqué, c'était en quelle classe ?",
  ),
  q(
    "etudes",
    "college",
    "point",
    "etudes-college-brevet",
    "Ton brevet, tu l'as passé quelle année ?",
  ),
  q(
    "etudes",
    "college",
    "point",
    "etudes-college-langue",
    "Ta première langue étrangère, tu l'as commencée quand ?",
  ),
  q(
    "etudes",
    "lycee",
    "periode",
    "etudes-lycee-annees",
    "Ton lycée, tu y es resté de quand à quand ?",
  ),
  q(
    "etudes",
    "lycee",
    "point",
    "etudes-lycee-bac",
    "Ton bac, c'était quelle année, et dans quelle filière ?",
  ),
  q(
    "etudes",
    "lycee",
    "point",
    "etudes-lycee-resultats",
    "Le jour des résultats du bac, tu étais où, et avec qui ?",
  ),
  q(
    "etudes",
    "lycee",
    "point",
    "etudes-lycee-annee-difficile",
    "Une année que tu as refaite, ou une classe qui t'a donné du fil à retordre ?",
  ),
  q(
    "etudes",
    "lycee",
    "point",
    "etudes-lycee-orientation",
    "Le moment où tu as choisi ta voie après le bac, c'était quand ?",
  ),
  q(
    "etudes",
    "lycee",
    "point",
    "etudes-lycee-prof",
    "Un prof du lycée qui a compté pour toi, c'était en quelle classe ?",
  ),
  q(
    "etudes",
    "etudes",
    "periode",
    "etudes-etudes-apres-bac",
    "Tes études après le bac, c'était où, et de quand à quand ?",
  ),
  q(
    "etudes",
    "etudes",
    "point",
    "etudes-etudes-diplome",
    "Ton premier diplôme après le bac, tu l'as eu quand ?",
  ),
  q(
    "etudes",
    "etudes",
    "periode",
    "etudes-etudes-etranger",
    "Un semestre ou une année d'études à l'étranger, c'était quand ?",
  ),
  q(
    "etudes",
    "etudes",
    "point",
    "etudes-etudes-examens",
    "Une session d'examens dont tu te souviens encore, c'était quelle année ?",
  ),
  q(
    "etudes",
    "etudes",
    "point",
    "etudes-etudes-changement-voie",
    "Tu as changé de voie pendant tes études ? C'était quand ?",
  ),
  q(
    "etudes",
    "etudes",
    "point",
    "etudes-etudes-memoire",
    "Un mémoire ou un projet de fin d'études, c'était sur quoi, et quand ?",
  ),
  q(
    "etudes",
    "etudes",
    "point",
    "etudes-etudes-remise-diplome",
    "Une remise de diplôme où ta famille est venue, c'était quand ?",
  ),
  q(
    "etudes",
    "adulte",
    "periode",
    "etudes-adulte-formation",
    "Une formation suivie une fois adulte, de quand à quand ?",
  ),
  q(
    "etudes",
    "adulte",
    "point",
    "etudes-adulte-autodidacte",
    "Un truc appris tout seul une fois adulte (une langue, un logiciel, un savoir-faire) ?",
  ),
  q(
    "etudes",
    "adulte",
    "periode",
    "etudes-adulte-reprise",
    "Tu as repris des études après avoir travaillé ? De quand à quand ?",
  ),
  q(
    "etudes",
    null,
    "point",
    "etudes-livre-marquant",
    "Un livre qui t'a fait voir les choses autrement, tu l'as lu quand ?",
  ),

  // --- Travail ---------------------------------------------------------------------------------
  q(
    "travail",
    "enfance",
    "point",
    "travail-enfance-metier-reve",
    "Petit, tu voulais faire quel métier ?",
  ),
  q("travail", "college", "point", "travail-college-stage-3e", "Ton stage de 3e, c'était où ?"),
  q("travail", "lycee", "point", "travail-lycee-job-ete", "Ton premier job d'été, c'était quoi ?"),
  q(
    "travail",
    "lycee",
    "periode",
    "travail-lycee-petits-boulots",
    "Du baby-sitting, des cours ou des petits boulots au lycée, de quand à quand ?",
  ),
  q(
    "travail",
    "lycee",
    "point",
    "travail-lycee-premier-salaire",
    "Ton tout premier salaire, tu en as fait quoi ?",
  ),
  q(
    "travail",
    "etudes",
    "periode",
    "travail-etudes-job-etudiant",
    "Un job étudiant qui t'a aidé à payer le loyer, de quand à quand ?",
  ),
  q(
    "travail",
    "etudes",
    "point",
    "travail-etudes-stage",
    "Ton premier stage pendant tes études, c'était où ?",
  ),
  q(
    "travail",
    "etudes",
    "periode",
    "travail-etudes-alternance",
    "Une alternance ou un apprentissage, c'était à quelle période ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-premier-poste",
    "Ton premier vrai poste, tu l'as commencé quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-premier-cdi",
    "Ton premier CDI, tu l'as signé quand ?",
  ),
  q(
    "travail",
    "adulte",
    "periode",
    "travail-adulte-plus-long-poste",
    "Ton plus long passage dans une même boîte, c'était de quand à quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-demission",
    "Une démission dont tu es fier, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-changement-metier",
    "Un vrai changement de métier, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-collegue-ami",
    "Un collègue devenu un ami, vous vous êtes rencontrés quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-promotion",
    "Une promotion ou une nouvelle responsabilité, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-projet-fier",
    "Le projet de travail dont tu es le plus fier, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "periode",
    "travail-adulte-a-son-compte",
    "Tu as lancé ta boîte ou travaillé à ton compte ? Depuis quand ?",
  ),
  q(
    "travail",
    "adulte",
    "periode",
    "travail-adulte-pause",
    "Une pause entre deux boulots, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-teletravail",
    "Le jour où tu as commencé à travailler de chez toi, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-entretien",
    "Un entretien d'embauche que tu n'oublieras pas, c'était quand ?",
  ),
  q(
    "travail",
    "adulte",
    "point",
    "travail-adulte-pot-depart",
    "Un pot de départ qui t'a ému, c'était quand ?",
  ),
  q(
    "travail",
    null,
    "periode",
    "travail-benevolat",
    "Du bénévolat ou un engagement dans une association, de quand à quand ?",
  ),
  q(
    "travail",
    null,
    "point",
    "travail-mentor",
    "Quelqu'un qui t'a appris ton métier, tu l'as rencontré quand ?",
  ),

  // --- Voyages ---------------------------------------------------------------------------------
  q(
    "voyages",
    "enfance",
    "point",
    "voyages-enfance-premieres-vacances",
    "Tes premières vacances dont tu te souviens, c'était où ?",
  ),
  q(
    "voyages",
    "enfance",
    "point",
    "voyages-enfance-mer",
    "La première fois que tu as vu la mer, tu avais quel âge ?",
  ),
  q("voyages", "enfance", "point", "voyages-enfance-colo", "Ta première colo, c'était où ?"),
  q(
    "voyages",
    "enfance",
    "periode",
    "voyages-enfance-camping",
    "Des vacances en camping avec ta famille, c'était à quelle période ?",
  ),
  q(
    "voyages",
    "college",
    "point",
    "voyages-college-voyage-scolaire",
    "Un voyage scolaire au collège, c'était où ?",
  ),
  q(
    "voyages",
    "college",
    "point",
    "voyages-college-correspondant",
    "Un échange avec un correspondant, c'était en quelle classe ?",
  ),
  q(
    "voyages",
    "college",
    "point",
    "voyages-college-neige",
    "Ta première fois à la neige, c'était quand ?",
  ),
  q(
    "voyages",
    "lycee",
    "point",
    "voyages-lycee-sans-parents",
    "Tes premières vacances sans tes parents, c'était où ?",
  ),
  q(
    "voyages",
    "lycee",
    "point",
    "voyages-lycee-voyage-classe",
    "Un voyage de classe au lycée, c'était où ?",
  ),
  q(
    "voyages",
    "etudes",
    "periode",
    "voyages-etudes-sac-a-dos",
    "Un voyage sac au dos pendant tes études, c'était où ?",
  ),
  q(
    "voyages",
    "etudes",
    "point",
    "voyages-etudes-road-trip",
    "Ton premier road trip entre amis, c'était quand ?",
  ),
  q(
    "voyages",
    "etudes",
    "periode",
    "voyages-etudes-ete-bouger",
    "Un été passé à aller de ville en ville (train, stop, covoiturage), c'était quelle année ?",
  ),
  q(
    "voyages",
    "adulte",
    "point",
    "voyages-adulte-plus-loin",
    "Le voyage le plus lointain que tu aies fait, c'était quand ?",
  ),
  q(
    "voyages",
    "adulte",
    "point",
    "voyages-adulte-seul",
    "Un voyage tout seul, c'était où, et quand ?",
  ),
  q(
    "voyages",
    "adulte",
    "point",
    "voyages-adulte-autre-continent",
    "La première fois sur un autre continent, c'était où ?",
  ),
  q(
    "voyages",
    "adulte",
    "point",
    "voyages-adulte-y-retourner",
    "Un pays où tu rêves de retourner, tu y étais allé quand ?",
  ),
  q(
    "voyages",
    "adulte",
    "periode",
    "voyages-adulte-vivre-etranger",
    "Tu as vécu à l'étranger ? De quand à quand ?",
  ),
  q(
    "voyages",
    null,
    "point",
    "voyages-avion",
    "Ta première fois en avion, c'était pour aller où ?",
  ),
  q(
    "voyages",
    null,
    "point",
    "voyages-train-bateau",
    "Un voyage en train de nuit ou en bateau dont tu te souviens ?",
  ),
  q(
    "voyages",
    null,
    "point",
    "voyages-galere",
    "Un voyage où tout est allé de travers, et que tu racontes encore ?",
  ),
  q(
    "voyages",
    null,
    "point",
    "voyages-ville-coup-de-coeur",
    "Une ville qui t'a donné envie d'y vivre, tu y étais quand ?",
  ),
  q(
    "voyages",
    null,
    "point",
    "voyages-fete-loin",
    "Un réveillon ou une fête passé loin de chez toi, c'était où ?",
  ),
  q(
    "voyages",
    null,
    "point",
    "voyages-sommet",
    "Une randonnée ou un sommet dont tu es fier, c'était quand ?",
  ),

  // --- Musique ---------------------------------------------------------------------------------
  q(
    "musique",
    "enfance",
    "point",
    "musique-enfance-premier-disque",
    "Le premier disque, la première cassette ou le premier CD qu'on t'a offert ?",
  ),
  q(
    "musique",
    "enfance",
    "point",
    "musique-enfance-generique",
    "Un générique de dessin animé que tu chantais par cœur, à quel âge ?",
  ),
  q(
    "musique",
    "enfance",
    "periode",
    "musique-enfance-instrument",
    "Un instrument appris enfant, de quand à quand ?",
  ),
  q(
    "musique",
    "enfance",
    "point",
    "musique-enfance-scene",
    "Une chorale ou une fête de l'école où tu as chanté sur scène ?",
  ),
  q(
    "musique",
    "college",
    "periode",
    "musique-college-groupe-prefere",
    "Ton groupe préféré au collège, c'était lequel ?",
  ),
  q(
    "musique",
    "college",
    "point",
    "musique-college-premier-album",
    "Le premier album acheté avec ton argent, c'était quand ?",
  ),
  q(
    "musique",
    "college",
    "point",
    "musique-college-poster",
    "Le poster au-dessus de ton lit au collège, c'était qui ?",
  ),
  q(
    "musique",
    "lycee",
    "point",
    "musique-lycee-premier-concert",
    "Ton premier concert, c'était qui, et vers quel âge ?",
  ),
  q(
    "musique",
    "lycee",
    "periode",
    "musique-lycee-groupe",
    "Tu as monté ou rejoint un groupe ? De quand à quand ?",
  ),
  q(
    "musique",
    "lycee",
    "point",
    "musique-lycee-fete-musique",
    "Une fête de la musique mémorable, c'était quelle année ?",
  ),
  q(
    "musique",
    "etudes",
    "point",
    "musique-etudes-festival",
    "Ton premier festival, c'était où, et quelle année ?",
  ),
  q(
    "musique",
    "etudes",
    "periode",
    "musique-etudes-sorties",
    "Une période où tu sortais danser presque chaque semaine ?",
  ),
  q(
    "musique",
    "etudes",
    "point",
    "musique-etudes-decouverte",
    "Un style de musique découvert pendant tes études, c'était quand ?",
  ),
  q(
    "musique",
    "adulte",
    "point",
    "musique-adulte-concert-marquant",
    "Le concert qui t'a le plus marqué une fois adulte, c'était quand ?",
  ),
  q(
    "musique",
    "adulte",
    "periode",
    "musique-adulte-instrument",
    "Un instrument repris ou commencé une fois adulte, depuis quand ?",
  ),
  q(
    "musique",
    null,
    "point",
    "musique-chanson-ete",
    "La chanson d'un été précis, c'était quelle année ?",
  ),
  q(
    "musique",
    null,
    "point",
    "musique-concert-seul",
    "Un concert où tu es allé seul, c'était quand ?",
  ),
  q(
    "musique",
    null,
    "periode",
    "musique-salle",
    "Une salle de concert où tu allais souvent, de quand à quand ?",
  ),
  q(
    "musique",
    null,
    "point",
    "musique-karaoke",
    "Un karaoké dont tout le monde parle encore, c'était quand ?",
  ),
  q(
    "musique",
    null,
    "point",
    "musique-artiste-croise",
    "Un artiste que tu as croisé ou rencontré, c'était quand ?",
  ),

  // --- Maisons ---------------------------------------------------------------------------------
  q(
    "maisons",
    "enfance",
    "periode",
    "maisons-enfance-maison",
    "La maison ou l'appartement de ton enfance, tu y as vécu de quand à quand ?",
  ),
  q(
    "maisons",
    "enfance",
    "periode",
    "maisons-enfance-chambre-partagee",
    "Ta chambre d'enfant, tu la partageais avec quelqu'un ?",
  ),
  q(
    "maisons",
    "enfance",
    "point",
    "maisons-enfance-demenagement",
    "Un déménagement quand tu étais petit, c'était quand ?",
  ),
  q(
    "maisons",
    "enfance",
    "point",
    "maisons-enfance-cabane",
    "Une cabane construite dans un jardin ou un bois, à quel âge ?",
  ),
  q(
    "maisons",
    "college",
    "point",
    "maisons-college-chambre-a-soi",
    "La première fois que tu as eu une chambre rien qu'à toi, c'était quand ?",
  ),
  q(
    "maisons",
    "college",
    "periode",
    "maisons-college-quartier",
    "Ton quartier quand tu étais au collège, tu y as vécu de quand à quand ?",
  ),
  q(
    "maisons",
    "lycee",
    "periode",
    "maisons-lycee-internat",
    "Un internat ou une chambre loin de chez tes parents au lycée ?",
  ),
  q(
    "maisons",
    "etudes",
    "periode",
    "maisons-etudes-premier-appart",
    "Ton premier appart ou ta première chambre d'étudiant, c'était où ?",
  ),
  q(
    "maisons",
    "etudes",
    "point",
    "maisons-etudes-quitter-parents",
    "Le jour où tu as quitté la maison de tes parents, c'était quand ?",
  ),
  q(
    "maisons",
    "etudes",
    "periode",
    "maisons-etudes-colocation",
    "Une colocation, de quand à quand ?",
  ),
  q(
    "maisons",
    "etudes",
    "periode",
    "maisons-etudes-residence",
    "Une chambre en résidence universitaire ? C'était à quelle période ?",
  ),
  q(
    "maisons",
    "adulte",
    "point",
    "maisons-adulte-premier-bail",
    "Le premier bail signé à ton nom, c'était quand ?",
  ),
  q(
    "maisons",
    "adulte",
    "point",
    "maisons-adulte-achat",
    "Ton premier achat immobilier, c'était quand ?",
  ),
  q(
    "maisons",
    "adulte",
    "periode",
    "maisons-adulte-travaux",
    "De gros travaux dans un logement, c'était quand ?",
  ),
  q(
    "maisons",
    "adulte",
    "periode",
    "maisons-adulte-ville",
    "Une ville où tu as vécu adulte, de quand à quand ?",
  ),
  q(
    "maisons",
    "adulte",
    "point",
    "maisons-adulte-jardin",
    "La première fois que tu as eu un jardin ou un balcon à toi ?",
  ),
  q(
    "maisons",
    "adulte",
    "point",
    "maisons-adulte-cremaillere",
    "Une pendaison de crémaillère dont tu te souviens, c'était quand ?",
  ),
  q(
    "maisons",
    "adulte",
    "point",
    "maisons-adulte-campagne",
    "Un départ de la ville pour la campagne, ou l'inverse, c'était quand ?",
  ),
  q(
    "maisons",
    null,
    "point",
    "maisons-demenagement-epique",
    "Le déménagement le plus épique de ta vie, c'était quand ?",
  ),
  q(
    "maisons",
    null,
    "periode",
    "maisons-logement-prefere",
    "Le logement où tu as été le plus heureux, de quand à quand ?",
  ),
  q(
    "maisons",
    null,
    "periode",
    "maisons-maison-vacances",
    "Une maison de vacances où tu retournais souvent, de quand à quand ?",
  ),
  q(
    "maisons",
    null,
    "point",
    "maisons-voisins",
    "Des voisins devenus des amis, c'était où, et quand ?",
  ),

  // --- Amis ------------------------------------------------------------------------------------
  q(
    "amis",
    "enfance",
    "point",
    "amis-enfance-meilleur-ami",
    "Ton meilleur ami ou ta meilleure amie d'enfance, vous vous êtes connus quand ?",
  ),
  q(
    "amis",
    "enfance",
    "point",
    "amis-enfance-anniversaire",
    "Une fête d'anniversaire avec tous tes copains, tu avais quel âge ?",
  ),
  q(
    "amis",
    "enfance",
    "periode",
    "amis-enfance-activite",
    "Un club, un sport ou une activité du mercredi, de quand à quand ?",
  ),
  q(
    "amis",
    "enfance",
    "periode",
    "amis-enfance-bande-quartier",
    "Une bande de copains de ton quartier, à quelle période ?",
  ),
  q(
    "amis",
    "college",
    "periode",
    "amis-college-bande",
    "Ta bande au collège, elle s'est formée quand ?",
  ),
  q(
    "amis",
    "college",
    "point",
    "amis-college-soiree-pyjama",
    "Ta première soirée pyjama ou ta première nuit chez un copain ?",
  ),
  q(
    "amis",
    "college",
    "periode",
    "amis-college-sport-equipe",
    "Un sport d'équipe au collège, de quand à quand ?",
  ),
  q(
    "amis",
    "lycee",
    "point",
    "amis-lycee-premiere-soiree",
    "Ta première vraie soirée entre amis, c'était quand ?",
  ),
  q(
    "amis",
    "lycee",
    "point",
    "amis-lycee-ami-encore",
    "Un ami du lycée que tu vois encore, vous vous êtes connus quand ?",
  ),
  q(
    "amis",
    "lycee",
    "point",
    "amis-lycee-vacances-bac",
    "Des vacances entre potes après le bac, c'était où ?",
  ),
  q(
    "amis",
    "lycee",
    "point",
    "amis-lycee-scooter",
    "Ton premier scooter, ou la première voiture d'un copain, c'était quand ?",
  ),
  q(
    "amis",
    "etudes",
    "point",
    "amis-etudes-integration",
    "Une soirée d'intégration ou un week-end de promo, c'était quand ?",
  ),
  q(
    "amis",
    "etudes",
    "periode",
    "amis-etudes-asso",
    "Une association étudiante où tu t'es investi, de quand à quand ?",
  ),
  q(
    "amis",
    "etudes",
    "point",
    "amis-etudes-ami-encore",
    "Un ami rencontré pendant tes études qui compte encore, c'était quand ?",
  ),
  q(
    "amis",
    "adulte",
    "point",
    "amis-adulte-temoin",
    "Le mariage d'un ami où tu étais témoin, c'était quand ?",
  ),
  q(
    "amis",
    "adulte",
    "periode",
    "amis-adulte-week-end-rituel",
    "Un week-end entre amis devenu un rituel, depuis quand ?",
  ),
  q(
    "amis",
    "adulte",
    "point",
    "amis-adulte-retrouvailles",
    "Des retrouvailles avec quelqu'un perdu de vue, c'était quand ?",
  ),
  q(
    "amis",
    "adulte",
    "point",
    "amis-adulte-nouvelle-ville",
    "Tes premiers amis dans une nouvelle ville, c'était quand ?",
  ),
  q(
    "amis",
    "adulte",
    "periode",
    "amis-adulte-club",
    "Un club ou une équipe une fois adulte, de quand à quand ?",
  ),
  q(
    "amis",
    null,
    "point",
    "amis-fete-surprise",
    "Une fête surprise, organisée ou reçue, c'était quand ?",
  ),
  q(
    "amis",
    null,
    "point",
    "amis-fou-rire",
    "Un fou rire entre amis dont tu te souviens encore, c'était quand ?",
  ),
  q(
    "amis",
    null,
    "point",
    "amis-amitie-retrouvee",
    "Une amitié qui s'est arrêtée puis a repris, c'était quand ?",
  ),
  q(
    "amis",
    null,
    "point",
    "amis-anniversaire-rond",
    "Un anniversaire rond (20, 30, 40 ans) fêté avec tes amis ?",
  ),
];
